import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { AdminAlertDto, AdminAlertKind } from '../../data-transfer-objects/admin-alert.dto';
import { AdminAlertsService } from '../../services/admin-alerts.service';
import { timeAgo } from '../map-pins';

/* What each kind is called on its tag, and which family colours it. */
const KINDS: Record<AdminAlertKind, { label: string; tone: 'abuse' | 'outreach' | 'space' }> = {
  abuse_flag: { label: 'Spam guard', tone: 'abuse' },
  abuse_report: { label: 'Report', tone: 'abuse' },
  content_flag: { label: 'Content', tone: 'abuse' },
  persona: { label: 'Persona', tone: 'abuse' },
  appeal: { label: 'Appeal', tone: 'abuse' },
  outreach_reply: { label: 'Reply', tone: 'outreach' },
  outreach_draft: { label: 'Draft', tone: 'outreach' },
  outreach_frozen: { label: 'Needs you', tone: 'outreach' },
  space_claimed: { label: 'Claimed', tone: 'space' },
};

/* Often enough that a tab left open notices a new case; rare enough not to matter. */
const POLL_MS = 2 * 60 * 1000;

/**
 * The alerts bell in the admin nav: what came in since this admin last
 * looked. Opening an alert or dismissing it clears it for this admin only;
 * the other admins keep theirs until they do the same.
 */
@Component({
  selector: 'app-admin-alerts',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      type="button"
      class="bell"
      [class.open]="open"
      (click)="toggle()"
      [attr.aria-label]="alerts.length ? alerts.length + ' new alerts' : 'Alerts'"
      aria-haspopup="true"
      [attr.aria-expanded]="open"
    >
      <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
        <path
          d="M12 3a6 6 0 0 0-6 6v3.6l-1.6 2.7A1 1 0 0 0 5.3 17h13.4a1 1 0 0 0 .9-1.7L18 12.6V9a6 6 0 0 0-6-6Zm-2.2 15.5a2.3 2.3 0 0 0 4.4 0"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span class="count" *ngIf="alerts.length">{{ alerts.length > 99 ? '99+' : alerts.length }}</span>
    </button>

    <div class="panel" *ngIf="open" role="dialog" [attr.aria-label]="showDismissed ? 'Dismissed alerts' : 'Alerts'">
      <ng-container *ngIf="!showDismissed; else dismissedView">
        <header>
          <b>Alerts</b>
          <button type="button" class="link" *ngIf="alerts.length" (click)="dismissAll()">
            Dismiss all
          </button>
        </header>

        <p class="empty" *ngIf="!alerts.length">Nothing new. You're all caught up.</p>

        <ul *ngIf="alerts.length">
          <li *ngFor="let a of alerts; trackBy: byId">
            <button type="button" class="item" (click)="go(a)">
              <span class="tag" [attr.data-tone]="kind(a).tone">{{ kind(a).label }}</span>
              <span class="title">{{ a.title }}</span>
              <span class="detail" *ngIf="a.detail">{{ a.detail }}</span>
              <span class="when">{{ ago(a.at) }}</span>
            </button>
            <button type="button" class="x" (click)="dismiss(a)" aria-label="Dismiss" title="Dismiss">×</button>
          </li>
        </ul>

        <footer>
          <button type="button" class="link" (click)="openDismissed()">See dismissed</button>
        </footer>
      </ng-container>

      <ng-template #dismissedView>
        <header>
          <button type="button" class="link back" (click)="showDismissed = false">‹ Alerts</button>
          <b>Dismissed</b>
        </header>

        <p class="empty" *ngIf="dismissedAlerts === null">Loading…</p>
        <p class="empty" *ngIf="dismissedError">Could not load dismissed alerts.</p>
        <p class="empty" *ngIf="dismissedAlerts?.length === 0 && !dismissedError">Nothing dismissed in the last 30 days.</p>

        <ul *ngIf="dismissedAlerts?.length">
          <li *ngFor="let a of dismissedAlerts; trackBy: byId" class="seen">
            <button type="button" class="item" (click)="goDismissed(a)">
              <span class="tag" [attr.data-tone]="kind(a).tone">{{ kind(a).label }}</span>
              <span class="title">{{ a.title }}</span>
              <span class="detail" *ngIf="a.detail">{{ a.detail }}</span>
              <span class="when">{{ ago(a.at) }}</span>
            </button>
          </li>
        </ul>
      </ng-template>
    </div>
  `,
  styles: [
    `
      :host {
        position: relative;
        display: inline-flex;
        font-family: 'Instrument Sans', system-ui, sans-serif;
      }
      .bell {
        position: relative;
        display: grid;
        place-items: center;
        width: 36px;
        height: 36px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: #12332f;
        cursor: pointer;
      }
      .bell:hover,
      .bell.open {
        background: rgba(18, 51, 47, 0.07);
      }
      .count {
        position: absolute;
        top: 2px;
        right: 0;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        box-sizing: border-box;
        border-radius: 999px;
        border: 2px solid #fff;
        background: #c2412d;
        color: #fff;
        font-size: 10.5px;
        font-weight: 700;
        line-height: 14px;
        text-align: center;
      }
      .panel {
        position: absolute;
        top: 44px;
        right: 0;
        z-index: 1000;
        width: min(380px, calc(100vw - 32px));
        max-height: min(520px, calc(100vh - 90px));
        display: flex;
        flex-direction: column;
        background: #fff;
        border: 1px solid rgba(18, 51, 47, 0.14);
        border-radius: 14px;
        box-shadow: 0 12px 36px rgba(18, 51, 47, 0.16);
        overflow: hidden;
      }
      header,
      footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid rgba(18, 51, 47, 0.1);
        font-size: 14px;
        color: #12332f;
      }
      footer {
        justify-content: center;
        padding: 10px 16px;
        border-top: 1px solid rgba(18, 51, 47, 0.1);
        border-bottom: 0;
      }
      header .back + b {
        flex: 1;
        text-align: center;
        margin-right: 52px;
      }
      .seen .item {
        padding-right: 16px;
      }
      .link {
        border: 0;
        background: none;
        padding: 0;
        font: inherit;
        font-size: 12.5px;
        font-weight: 600;
        color: #1f6f66;
        cursor: pointer;
      }
      .link:hover {
        text-decoration: underline;
      }
      .empty {
        margin: 0;
        padding: 28px 16px;
        text-align: center;
        font-size: 13px;
        color: #5e5a57;
      }
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        overflow-y: auto;
      }
      li {
        position: relative;
        border-bottom: 1px solid rgba(18, 51, 47, 0.07);
      }
      li:last-child {
        border-bottom: 0;
      }
      .item {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 3px 8px;
        width: 100%;
        padding: 11px 40px 11px 16px;
        border: 0;
        background: none;
        text-align: left;
        font: inherit;
        color: #12332f;
        cursor: pointer;
      }
      .item:hover {
        background: rgba(18, 51, 47, 0.04);
      }
      .tag {
        align-self: start;
        margin-top: 1px;
        padding: 1px 7px;
        border-radius: 999px;
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 0.02em;
        white-space: nowrap;
      }
      .tag[data-tone='abuse'] {
        background: #fbe6e1;
        color: #9a2f1f;
      }
      .tag[data-tone='outreach'] {
        background: #e1f0ee;
        color: #1f6f66;
      }
      .tag[data-tone='space'] {
        background: #ece8f7;
        color: #4b3c8f;
      }
      .title {
        font-size: 13.5px;
        font-weight: 600;
        overflow-wrap: anywhere;
      }
      .detail,
      .when {
        grid-column: 2;
        font-size: 12.5px;
        color: #5e5a57;
      }
      .detail {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
      }
      .when {
        font-size: 11.5px;
        color: #8a8582;
      }
      .x {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 26px;
        height: 26px;
        border: 0;
        border-radius: 999px;
        background: none;
        color: #8a8582;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;
      }
      .x:hover {
        background: rgba(18, 51, 47, 0.08);
        color: #12332f;
      }
    `,
  ],
})
export class AdminAlertsComponent implements OnInit, OnDestroy {
  alerts: AdminAlertDto[] = [];
  open = false;
  /** The panel's second view: what this admin already dismissed. */
  showDismissed = false;
  dismissedAlerts: AdminAlertDto[] | null = null;
  dismissedError = false;
  private readonly subs = new Subscription();

  constructor(
    private readonly service: AdminAlertsService,
    private readonly router: Router,
    private readonly host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.subs.add(this.service.alerts$.subscribe((a) => (this.alerts = a)));
    this.service.refresh();
    this.subs.add(
      interval(POLL_MS).subscribe(() => {
        if (document.visibilityState === 'visible') this.service.refresh();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  toggle(): void {
    this.open = !this.open;
    this.showDismissed = false;
    if (this.open) this.service.refresh();
  }

  /** Fresh each time: something dismissed a moment ago belongs at the top. */
  openDismissed(): void {
    this.showDismissed = true;
    this.dismissedAlerts = null;
    this.dismissedError = false;
    this.service.dismissed().subscribe({
      next: (a) => (this.dismissedAlerts = a),
      error: () => {
        this.dismissedAlerts = [];
        this.dismissedError = true;
      },
    });
  }

  /** Already dismissed, so opening one only navigates. */
  goDismissed(a: AdminAlertDto): void {
    this.open = false;
    this.router.navigateByUrl(a.link);
  }

  /** Opening one is reading it: it leaves this admin's bell. */
  go(a: AdminAlertDto): void {
    this.open = false;
    this.service.dismiss([a.id]);
    this.router.navigateByUrl(a.link);
  }

  dismiss(a: AdminAlertDto): void {
    this.service.dismiss([a.id]);
  }

  /* Only what is on screen: anything arriving after the panel opened stays. */
  dismissAll(): void {
    this.service.dismiss(this.alerts.map((a) => a.id));
  }

  kind(a: AdminAlertDto) {
    return KINDS[a.kind] ?? { label: 'Alert', tone: 'space' as const };
  }

  ago(at: string): string {
    return timeAgo(at) ?? '';
  }

  byId(_: number, a: AdminAlertDto): string {
    return a.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // The path, not contains(): switching views removes the clicked button
    // from the DOM before this runs, and a detached target reads as outside.
    if (this.open && !event.composedPath().includes(this.host.nativeElement)) this.open = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open = false;
  }
}
